import { useCallback, useMemo } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'

import { isAuthConfigured } from '@/lib/env'
import { signIn, signOut } from '@/lib/auth/msal'
import { useLocalSessionStore } from '@/stores/local-session.store'

export interface AuthUser {
  id: string
  name: string
  email: string
  /** App roles assigned in the Entra ID app registration. */
  roles: string[]
}

export interface UseAuthResult {
  isAuthenticated: boolean
  isReady: boolean
  user: AuthUser | null
  login: () => Promise<void>
  logout: () => Promise<void>
  hasRole: (role: string) => boolean
}

interface EntraIdTokenClaims {
  oid?: string
  sub?: string
  Nev?: string
  preferred_username?: string
  email?: string
  roles?: string[]
}

/** Placeholder identity for the unconfigured local mode. */
const LOCAL_USER: AuthUser = {
  id: '__local__',
  name: 'Local user',
  email: 'local@uni-corvinus.hu',
  roles: [],
}

/** The single hook the app uses for identity — never import MSAL in a component. */
export function useAuth(): UseAuthResult {
  const { instance, inProgress, accounts } = useMsal()
  const msalAuthenticated = useIsAuthenticated()

  const localSignedIn = useLocalSessionStore((state) => state.signedIn)
  const localSignIn = useLocalSessionStore((state) => state.signIn)
  const localSignOut = useLocalSessionStore((state) => state.signOut)

  const msalUser = useMemo<AuthUser | null>(() => {
    const account = instance.getActiveAccount() ?? accounts[0]
    if (!account) return null

    const claims = (account.idTokenClaims ?? {}) as EntraIdTokenClaims
    return {
      id: claims.oid ?? claims.sub ?? account.homeAccountId,
      name: account.name ?? claims.Nev ?? '',
      email: claims.preferred_username ?? claims.email ?? account.username,
      roles: claims.roles ?? [],
    }
  }, [instance, accounts])

  const user = isAuthConfigured ? msalUser : localSignedIn ? LOCAL_USER : null
  const hasRole = useCallback((role: string) => user?.roles.includes(role) ?? false, [user])

  const login = useCallback(async (): Promise<void> => {
    if (isAuthConfigured) {
      await signIn()
      return
    }
    localSignIn()
  }, [localSignIn])

  const logout = useCallback(async (): Promise<void> => {
    if (isAuthConfigured) {
      await signOut()
      return
    }
    localSignOut()
  }, [localSignOut])

  return {
    isAuthenticated: isAuthConfigured ? msalAuthenticated : localSignedIn,
    isReady: isAuthConfigured ? inProgress === 'none' : true,
    user,
    login,
    logout,
    hasRole,
  }
}
